import QuizTypes from 'types/QuizTypes';

export const scoringQuizTypes = [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak];
export const pollQuizTypes = [QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector];

interface IQuizTypeDefaults {
  registrationCaption: string;
  landingPage: {
    buttonTitle: string;
    title1?: string;
    title2?: string;
    title3?: string;
  };
  publicLeaderboard?: {
    headerText?: string;
    quizQRCodeTitle?: string;
  };
  callToAction?: {
    resultButtonTitle?: string;
    title?: string;
  };
}

// Per-type copy/defaults used when seeding a brand new quiz. Shared between
// the manual QuizFormDialog flow and the QuizAgentDialog draftQuiz seed so
// both start from the same defaults instead of always assuming Scoring.
export const quizTypeDefaults: Record<QuizTypes, IQuizTypeDefaults> = {
  [QuizTypes.Personality]: {
    registrationCaption: 'Register now to see your personalized result!',
    landingPage: {
      buttonTitle: 'Play now',
      title1: 'Sample Text 1',
      title2: '',
      title3: ''
    }
  },
  [QuizTypes.Poll]: {
    registrationCaption: 'Register now to see poll results!',
    landingPage: {
      buttonTitle: 'Vote now',
      title1: 'WHO IS YOUR',
      title2: 'PLAYER',
      title3: 'OF THE YEAR'
    },
    publicLeaderboard: {
      headerText: 'Results',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'View Results',
      title: 'Thank you for Registering!'
    }
  },
  [QuizTypes.Prediction]: {
    registrationCaption: 'Register now to see how your predictions scored!',
    landingPage: {
      buttonTitle: 'Play now',
      title1: 'Sample Text 1',
    },
    publicLeaderboard: {
      headerText: 'Leaderboard',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'See how you placed'
    }
  },
  [QuizTypes.Scoring]: {
    registrationCaption: 'Thanks for playing. Register for a chance to win',
    landingPage: {
      buttonTitle: 'Play now',
      title1: 'Sample Text 1',
    },
    publicLeaderboard: {
      headerText: 'Leaderboard',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'See how you placed'
    }
  },
  [QuizTypes.Survey]: {
    registrationCaption: 'Register Now and Make Your Feedback Count!',
    landingPage: {
      buttonTitle: 'Take our Survey',
      title1: 'Your Opinion Matters',
      title2: 'Take a Moment',
      title3: 'To Share it with us!'
    },
    callToAction: {
      title: 'Thank you for Registering!'
    }
  },
  [QuizTypes.RankingPoll]: {
    registrationCaption: 'Register now to see ranking poll results!',
    landingPage: {
      buttonTitle: 'Vote now',
      title1: 'Sample Text 1',
    },
    publicLeaderboard: {
      headerText: 'Results',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'View Results',
      title: 'Thank you for Registering!'
    }
  },
  [QuizTypes.TeamSelector]: {
    registrationCaption: 'Register now to see your team!',
    landingPage: {
      buttonTitle: 'Vote now',
      title1: 'Sample Text 1',
    },
    publicLeaderboard: {
      headerText: 'Results',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'View Results'
    }
  },
  [QuizTypes.BeatTheExpert]: {
    registrationCaption: 'Register now to see if you can beat the expert!',
    landingPage: {
      buttonTitle: 'Play now',
      title1: "Today's expert is ______",
    },
    publicLeaderboard: {
      headerText: 'Leaderboard',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'See how you placed'
    }
  },
  [QuizTypes.Streak]: {
    registrationCaption: 'Register now to see if you can maintain your streak!',
    landingPage: {
      buttonTitle: 'Play now',
      title1: 'Sample Text 1',
    },
    publicLeaderboard: {
      headerText: 'Leaderboard',
      quizQRCodeTitle: 'Scan To Play'
    },
    callToAction: {
      resultButtonTitle: 'See how you placed'
    }
  }
};

// Builds the type-dependent slice of a new quiz's default field values
// (landing page copy, registration caption, leaderboard/CTA copy, results
// page styling). Intended to be shallow-merged over the generic
// `initialQuizForm` defaults — it only returns fields that actually vary by
// quiz type.
export const buildQuizTypeDefaults = (type: QuizTypes) => {
  const defaults = quizTypeDefaults[type];
  const isScoringQuizzes = scoringQuizTypes.includes(type);
  const isPollQuizzes = pollQuizTypes.includes(type);

  const result: any = {
    landingPage: {
      buttonTitle: defaults.landingPage.buttonTitle,
      title1: defaults.landingPage.title1 ?? '',
      title2: defaults.landingPage.title2 ?? '',
      title3: defaults.landingPage.title3 ?? '',
    },
    registrationPage: {
      caption: defaults.registrationCaption,
    },
    countdownPage: {
      enabled: isScoringQuizzes,
      text1: isScoringQuizzes ? 'Answer fast, score big!' : '',
    },
    resultsPage: {
      disabledPageText: isScoringQuizzes
        ? "Thanks for playing!\nThe leaderboard is turned off for this game. We’ll share the final results on our social media and contact you by email if you’re a winner!"
        : "Thanks for taking part!\nThe results for this poll are currently hidden. We’ll share the final outcome on our social media once voting is complete.",
      placingGradientStart: isPollQuizzes ? '#109be0' : '#be7eea',
      placingGradientEnd: isPollQuizzes ? '#11e6f3' : '#687fd9',
      currentPlacingGradientStart: '#e3722d',
      currentPlacingGradientEnd: '#f4d549',
    },
  };

  if (![QuizTypes.Personality, QuizTypes.Survey].includes(type)) {
    result.publicLeaderboard = {
      headerText: defaults.publicLeaderboard?.headerText ?? '',
      quizQRCodeTitle: defaults.publicLeaderboard?.quizQRCodeTitle ?? '',
    };
    result.callToAction = {
      resultButtonTitle: defaults.callToAction?.resultButtonTitle ?? '',
      title: defaults.callToAction?.title ?? '',
    };
  }

  if (type === QuizTypes.TeamSelector) {
    result.resultsPage.primaryColor = 'rgb(227,0,19)';
  }

  return result;
};
